/* ==========================================================================
   DIREVULF — theme.js
   Vanilla JS, loaded with `defer`. No framework, no third-party runtime.
   Every module is guarded: a missing element must never throw.

   What lives here: the shared overlay controller (native <dialog> drawers and
   sheets, plus the older hidden-attribute overlays some scripts still use),
   the live-region announcer, the toast, delegated open/close triggers, the
   header count badges, and the product card's quick add. Cart, wishlist,
   filters, search and delivery keep their own files.
   ========================================================================== */
(function () {
  'use strict';

  var FOCUSABLE = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function focusableIn(el) {
    return $$(FOCUSABLE, el).filter(function (n) {
      return n.offsetWidth > 0 || n.offsetHeight > 0 || n === document.activeElement;
    });
  }

  /* ------------------------------------------------ focus ring modality */
  /* THE FOCUS RING IS FOR THE KEYBOARD — EVERYWHERE, NOT ONE COMPONENT AT A
     TIME.

     Several things in this theme move focus by script after a tap or click:
     the overlay controller puts focus in a drawer when it opens and gives it
     back to the button that opened it when it closes (below), and carousel.js
     focuses a rail's track on press so the page does not jump. Browsers draw
     :focus-visible for script focus they cannot attribute to a pointer, and
     WebKit — Safari and every browser on iOS — does so after a tap almost
     every time. That is the black rectangle round the Men / Women tiles after
     a swipe, and round the Menu button after the menu is closed.

     So focus that arrives while the last input was a pointer is labelled
     data-dv-pointer-focus, and the stylesheet (storefront-15.css) hides the
     ring only while that label is present. Any key press, and focus leaving,
     removes it — a keyboard user always sees exactly the ring they saw
     before. Text fields are never labelled: their focus indication is the
     field itself and it must stay. This is the same attribute carousel.js
     already sets on its arrows, now applied in one place for every control. */
  (function focusModality() {
    var pointer = false;
    function isTextField(el) {
      if (el.isContentEditable) return true;
      var tag = el.tagName;
      if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if (tag !== 'INPUT') return false;
      return !/^(checkbox|radio|range|button|submit|reset|image|color|file)$/i.test(el.type || '');
    }
    function onPointer() { pointer = true; }
    document.addEventListener('pointerdown', onPointer, true);
    document.addEventListener('mousedown', onPointer, true);
    document.addEventListener('touchstart', onPointer, { capture: true, passive: true });
    document.addEventListener('keydown', function (event) {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      pointer = false;
      var active = document.activeElement;
      if (active && active.removeAttribute) active.removeAttribute('data-dv-pointer-focus');
    }, true);
    document.addEventListener('focusin', function (event) {
      var el = event.target;
      if (!pointer || !el || !el.setAttribute || el === document.body) return;
      if (isTextField(el)) return;
      el.setAttribute('data-dv-pointer-focus', '');
    }, true);
    document.addEventListener('focusout', function (event) {
      var el = event.target;
      if (el && el.removeAttribute) el.removeAttribute('data-dv-pointer-focus');
    }, true);
  })();

  function announce(message) {
    var region = document.getElementById('dv-live-region');
    if (!region || !message) return;
    region.textContent = '';
    window.setTimeout(function () { region.textContent = message; }, 60);
  }

  /* ------------------------------------------------------------------ toast */
  var toastTimer;
  function toast(message, ms) {
    var node = $('[data-dv-toast]');
    if (!node || !message) return;
    node.textContent = message;
    node.hidden = false;
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(function () { node.hidden = true; }, ms || 2600);
    announce(message);
  }

  /* --------------------------------------------------- overlay controller */
  /* One controller for everything that opens over the page. A <dialog>
     (drawers, sheets, the PIN dialog, the size guide) is opened with
     showModal(), which gives the focus trap, Escape and the backdrop for
     free; an element that is not a dialog is shown by clearing `hidden` and
     trapped here, as before. Either way focus returns to the trigger. */
  var Overlay = {
    active: null,
    trigger: null,
    lockedScroll: '',

    resolve: function (el) {
      if (!el) return null;
      if (el.tagName === 'DIALOG') return el;
      var host = el.closest && el.closest('dialog');
      return host || el;
    },

    open: function (target, trigger) {
      var el = Overlay.resolve(target);
      if (!el) return;
      if (Overlay.active && Overlay.active !== el) Overlay.close();
      Overlay.active = el;
      Overlay.trigger = trigger || document.activeElement;
      if (el.tagName === 'DIALOG') {
        if (!el.open) {
          try { el.showModal(); } catch (e) { el.setAttribute('open', ''); }
        }
      } else {
        el.hidden = false;
      }
      Overlay.lockedScroll = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      if (trigger && trigger.setAttribute) trigger.setAttribute('aria-expanded', 'true');
      window.requestAnimationFrame(function () {
        var focusTarget = el.querySelector('[data-autofocus]') || focusableIn(el)[0];
        if (focusTarget) focusTarget.focus();
      });
      document.dispatchEvent(new CustomEvent('dv:overlay:open', { detail: { el: el } }));
    },

    close: function () {
      var el = Overlay.active;
      if (!el) return;
      if (el.tagName === 'DIALOG') { if (el.open) el.close(); }
      else el.hidden = true;
      document.body.style.overflow = Overlay.lockedScroll || '';
      if (Overlay.trigger && Overlay.trigger.setAttribute) {
        Overlay.trigger.setAttribute('aria-expanded', 'false');
        if (document.body.contains(Overlay.trigger)) Overlay.trigger.focus();
      }
      Overlay.active = null;
      Overlay.trigger = null;
      document.dispatchEvent(new CustomEvent('dv:overlay:close', { detail: { el: el } }));
    },

    trap: function (event) {
      var el = Overlay.active;
      if (!el || el.tagName === 'DIALOG' || event.key !== 'Tab') return;
      var items = focusableIn(el);
      if (!items.length) { event.preventDefault(); return; }
      var first = items[0];
      var last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  };
  window.DirevulfOverlay = Overlay;

  /* A dialog closed natively (Escape, form method=dialog) must leave the
     controller in the same state as one closed through it. */
  document.addEventListener('close', function (event) {
    if (event.target && event.target === Overlay.active) {
      document.body.style.overflow = Overlay.lockedScroll || '';
      if (Overlay.trigger && Overlay.trigger.setAttribute) {
        Overlay.trigger.setAttribute('aria-expanded', 'false');
        if (document.body.contains(Overlay.trigger)) Overlay.trigger.focus();
      }
      Overlay.active = null;
      Overlay.trigger = null;
    }
  }, true);

  /* Clicking the backdrop closes a dialog: the dialog element itself is the
     click target only outside its content box. */
  document.addEventListener('click', function (event) {
    var dialog = event.target;
    if (!dialog || dialog.tagName !== 'DIALOG' || !dialog.open) return;
    var box = dialog.getBoundingClientRect();
    var inside = event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
    if (!inside) { Overlay.active = dialog; Overlay.close(); }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && Overlay.active && Overlay.active.tagName !== 'DIALOG') {
      event.preventDefault();
      Overlay.close();
      return;
    }
    Overlay.trap(event);
  });

  /* Delegated open/close triggers: data-dv-open="#id" / data-dv-close */
  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-dv-open]');
    if (opener) {
      var sel = opener.getAttribute('data-dv-open');
      var target = sel && document.querySelector(sel);
      if (target) {
        event.preventDefault();
        if (Overlay.active === Overlay.resolve(target)) Overlay.close();
        else Overlay.open(target, opener);
      }
      return;
    }
    if (event.target.closest('[data-dv-close]')) {
      event.preventDefault();
      var host = event.target.closest('dialog');
      if (host && host !== Overlay.active) { Overlay.active = host; }
      Overlay.close();
    }
  });

  /* ------------------------------------------------------ announcement bar */
  function initAnnouncement() {
    var bar = $('[data-dv-announcement]');
    if (!bar) return;
    var key = 'dv:announcement:' + (bar.getAttribute('data-key') || 'default');
    try {
      if (window.localStorage.getItem(key) === 'dismissed') { bar.hidden = true; return; }
    } catch (e) { /* storage unavailable — show the bar */ }
    var dismiss = $('[data-dv-announcement-dismiss]', bar);
    if (!dismiss) return;
    dismiss.addEventListener('click', function () {
      bar.hidden = true;
      try { window.localStorage.setItem(key, 'dismissed'); } catch (e) { /* ignore */ }
    });
  }

  /* -------------------------------------------------------------- quick add */
  /* The card's QUICK ADD button reveals the size row; a size adds that
     variant through the one add-to-cart implementation in cart.js. A product
     with a single variant adds straight away. Nothing here reads a price or a
     stock figure: sold-out sizes arrive disabled from Liquid. */
  function initQuickAdd() {
    document.addEventListener('click', function (event) {
      var toggle = event.target.closest('[data-dv-quick-add]');
      if (toggle) {
        event.preventDefault();
        var card = toggle.closest('[data-dv-product-card]');
        var panel = card && $('[data-dv-quick-sizes]', card);
        var single = toggle.getAttribute('data-variant-id');
        if (single && !panel) { addVariant(single, toggle); return; }
        if (!panel) return;
        var open = panel.hidden;
        $$('[data-dv-quick-sizes]').forEach(function (p) { p.hidden = true; });
        $$('[data-dv-quick-add]').forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
        panel.hidden = !open;
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) { var first = $('button:not([disabled])', panel); if (first) first.focus(); }
        return;
      }
      var size = event.target.closest('[data-dv-quick-size]');
      if (size) {
        event.preventDefault();
        addVariant(size.getAttribute('data-variant-id'), size);
        return;
      }
      if (!event.target.closest('[data-dv-quick-sizes]')) {
        $$('[data-dv-quick-sizes]').forEach(function (p) { p.hidden = true; });
        $$('[data-dv-quick-add][aria-expanded="true"]').forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var openPanel = $('[data-dv-quick-sizes]:not([hidden])');
      if (!openPanel) return;
      openPanel.hidden = true;
      var card = openPanel.closest('[data-dv-product-card]');
      var toggle = card && $('[data-dv-quick-add]', card);
      if (toggle) { toggle.setAttribute('aria-expanded', 'false'); toggle.focus(); }
    });
  }

  function addVariant(variantId, button) {
    if (!variantId || !window.DirevulfCart) return;
    var form = document.createElement('form');
    var id = document.createElement('input');
    id.name = 'id'; id.value = variantId;
    var qty = document.createElement('input');
    qty.name = 'quantity'; qty.value = '1';
    form.appendChild(id); form.appendChild(qty);
    if (button) button.setAttribute('aria-busy', 'true');
    window.DirevulfCart.add(form).finally(function () {
      if (button) button.removeAttribute('aria-busy');
      $$('[data-dv-quick-sizes]').forEach(function (p) { p.hidden = true; });
    });
  }

  /* ----------------------------------------------------------- simple tabs */
  /* The mobile drawer's MEN / WOMEN tabs switch which category grid shows.
     Buttons carry data-dv-tab="key"; panels carry data-dv-tabpanel="key". */
  function initTabs() {
    document.addEventListener('click', function (event) {
      var tab = event.target.closest('[data-dv-tab]');
      if (!tab) return;
      var key = tab.getAttribute('data-dv-tab');
      var list = tab.closest('[role="tablist"]');
      var scope = (list && list.parentElement) || document;
      $$('[data-dv-tab]', list || document).forEach(function (t) {
        t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
      });
      $$('[data-dv-tabpanel]', scope).forEach(function (panel) {
        panel.hidden = panel.getAttribute('data-dv-tabpanel') !== key;
      });
    });
  }

  /* ------------------------------------------------------ delivery location */
  /* The header's location button repeats what the PIN check stored on the
     cart. When delivery.js quotes a pincode it announces it, and the label
     becomes "Deliver to <PIN>" — the PIN the shopper typed, nothing more.
     Only a pincode Shopify actually priced changes the label: a refused one
     (announced with no rate) would otherwise read as a delivery location the
     shopper never got. */
  function initLocation() {
    document.addEventListener('dv:delivery:quote', function (event) {
      var pin = event.detail && event.detail.pin;
      if (!pin || !event.detail.rate) return;
      $$('[data-dv-location]').forEach(function (btn) {
        var label = $('[data-dv-location-label]', btn);
        if (label) label.textContent = (btn.getAttribute('data-label-deliver') || 'Deliver to') + ' ' + pin;
      });
    });
  }

  /* ------------------------------------------------------------------ init */
  function init() {
    initAnnouncement();
    initQuickAdd();
    initTabs();
    initLocation();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  window.Direvulf = { announce: announce, toast: toast, $: $, $$: $$, Overlay: Overlay };
})();